import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, BarChart, ArrowRight, CheckCircle } from 'lucide-react';

const Program: React.FC = () => {
  return (
    <div>
      {/* Hero */}
      <div className="relative bg-stone-900 text-orange-50 py-32 px-6">
         <img src="https://picsum.photos/1600/600?grayscale" alt="Program" className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-overlay" />
         <div className="relative max-w-4xl mx-auto text-center space-y-6">
            <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest backdrop-blur-sm">Now Accepting New Clients</span>
            <h1 className="text-4xl md:text-6xl font-serif text-white">90-Day Business Growth Program</h1>
            <p className="text-xl text-orange-100/90 max-w-3xl mx-auto leading-relaxed">
                Our signature 90-Day Business Growth Engine™ is a structured, yet flexible coaching program designed specifically for corporate women who are ready to install the systems and structures to operate their service-based business strategically.
            </p>
            <div className="pt-8">
                <Link to="/contact" className="bg-orange-100 text-stone-900 px-8 py-4 rounded font-bold hover:bg-white transition-colors inline-block">
                    Take the First Step
                </Link>
            </div>
         </div>
      </div>

      {/* What happens in 90 days? */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
          <h2 className="text-3xl font-serif mb-12 text-center text-stone-900">What happens in 90 days?</h2>
          <div className="grid md:grid-cols-3 gap-8">
              {/* Month 1 */}
              <div className="p-8 bg-white rounded-lg border border-orange-100 shadow-sm flex flex-col">
                  <div className="mb-6 bg-orange-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto text-orange-700">
                    <Calendar size={32} />
                  </div>
                  <h3 className="font-bold text-xl mb-4 text-stone-900 text-center">Month 1: Clarity & Foundation</h3>
                  <ul className="space-y-3 text-stone-600 text-sm flex-grow">
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Clarify your strengths and transferable skills</li>
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Define your niche & positioning</li>
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Identify your ideal client</li>
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Business vision & direction</li>
                  </ul>
              </div>

              {/* Month 2 */}
              <div className="p-8 bg-white rounded-lg border border-orange-100 shadow-sm flex flex-col">
                  <div className="mb-6 bg-orange-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto text-orange-700">
                    <Users size={32} />
                  </div>
                  <h3 className="font-bold text-xl mb-4 text-stone-900 text-center">Month 2: Offer & Digital Presence</h3>
                  <ul className="space-y-3 text-stone-600 text-sm flex-grow">
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Create your core offer</li>
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Pricing & value positioning</li>
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Personal brand clarity</li>
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Website & digital presence strategy</li>
                  </ul>
              </div>

              {/* Month 3 */}
              <div className="p-8 bg-white rounded-lg border border-orange-100 shadow-sm flex flex-col">
                  <div className="mb-6 bg-orange-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto text-orange-700">
                    <BarChart size={32} />
                  </div>
                  <h3 className="font-bold text-xl mb-4 text-stone-900 text-center">Month 3: Launch & Support</h3>
                  <ul className="space-y-3 text-stone-600 text-sm flex-grow">
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Launch your Minimum Viable Offer</li>
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Client attraction basics</li>
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Feedback & refinement</li>
                      <li className="flex items-start gap-2"><span className="text-orange-500">•</span> Accountability and mindset support</li>
                  </ul>
              </div>
          </div>
      </section>

      {/* Curriculum Details */}
      <section className="bg-orange-100 py-24 px-6">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
              <div className="space-y-8">
                  <h2 className="text-4xl font-serif text-stone-900">A journey built for execution, not just learning.</h2>
                  <p className="text-stone-700 text-lg">Most programs give you information. We give you implementation. Every week includes a workshop where we actually build the assets your business needs.</p>
                  
                  <h3 className="text-2xl font-serif text-stone-900 pt-4">What You'll Achieve in 12 weeks</h3>
                  
                  <div className="space-y-4 bg-white p-8 rounded-xl border border-orange-200 shadow-sm">
                      {[
                        { week: "Week 1", title: "Discover your Freedom Number (the exact income target that funds your dream lifestyle)" },
                        { week: "Week 2", title: "Identify transferable skills and overcome mindset blocks that hold you back from growth" },
                        { week: "Week 3", title: "Identify your niche and design a lifestyle-first business model that supports your health, relationships, and personal goals" },
                        { week: "Week 4", title: "Define your dream client and learn how to attract them consistently" },
                        { week: "Week 5", title: "Create your signature, income-generating offer" },
                        { week: "Week 6", title: "Package and price your services for value (not hours)" },
                        { week: "Week 7", title: "Establish your digital presence with a professional website to build trust" },
                        { week: "Week 8", title: "Design a successful ATTRACT → ACTION marketing system to get your client's attention and guide them to make a purchase." },
                        { week: "Week 9", title: "Automate or outsource low-value tasks so you can reclaim your hours each month" },
                        { week: "Week 10", title: "Launch your Minimum Viable Offer (MVO) with confidence and accountability" },
                        { week: "Week 11-12", title: "Fix leaks in your marketing pipeline (Attract → Engage → Nurture → Action) using your own data." }
                      ].map((item, i) => (
                          <div key={i} className="flex gap-4 items-start border-b border-orange-100 pb-3 last:border-0 last:pb-0">
                              <span className="font-bold text-orange-600 text-sm whitespace-nowrap min-w-[80px]">{item.week}</span>
                              <span className="text-stone-700 text-sm">{item.title}</span>
                          </div>
                      ))}
                  </div>
                  <p className="text-stone-600 italic border-l-4 border-orange-400 pl-4 py-2 bg-white rounded-r">
                    The 90-Day Business Growth Program is designed to provide a safe space to ask questions and grow. The coaching sessions will be adapted to your unique needs, experiences, and goals.
                  </p>
              </div>
              <div className="relative sticky top-24 self-start">
                  <img src="https://images.unsplash.com/photo-1664575602276-acd073f104c1?q=80&w=800&auto=format&fit=crop" alt="Smiling professional woman working at desk" className="rounded-lg shadow-xl" />
                  <div className="absolute -bottom-6 -left-6 bg-white p-8 rounded shadow-lg max-w-sm border border-orange-100 hidden md:block">
                      <p className="font-serif italic text-xl mb-4 text-stone-900">"With too many ideas, I was overwhelmed and making no progress. The 12-week sprint gave me the structure I needed."</p>
                  </div>
              </div>
          </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 text-center">
          <h2 className="text-4xl font-serif mb-6 text-stone-900">Serious about building your business?</h2>
          <p className="text-stone-500 mb-8">Spaces are limited to 15 founders per cohort to ensure personal attention.</p>
          <Link to="/contact" className="bg-stone-900 text-white px-10 py-4 rounded text-lg font-medium hover:bg-stone-800 transition-colors inline-flex items-center gap-2">
              Apply Now <ArrowRight size={20} />
          </Link>
      </section>
    </div>
  );
};

export default Program;